import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store';
import { useCompaniesApi } from '../api';
import { resetError, setError, setLoading, setPaymentMethodConfigured, setOnboardingStatus } from '../slices';
import { CreateAdminCompanyRequestData, GetStripeCompanyPortalLinkParams } from '../types';

export const useAdminCompanyService = () => {
  const dispatch = useDispatch();
  const { loading, error } = useAppSelector(state => state.companies);

  const api = useCompaniesApi();

  const createCompany = useCallback(async (data: CreateAdminCompanyRequestData) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ create: true }));

      return await api.createAdminCompany(data);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ create: false }));
    }
  }, []);

  const deleteCompany = useCallback(async (companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ delete: true }));

      await api.deleteAdminCompany(companyId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ delete: false }));
    }
  }, []);

  const getStripeCompanyPortalLink = useCallback(
    async (companyId: string, params?: GetStripeCompanyPortalLinkParams) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ getStripeCompanyPortalLink: true }));

        return await api.getStripeCompanyPortalLink(companyId, params);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ getStripeCompanyPortalLink: false }));
      }
    },
    [],
  );
  const getCompanyPaymentMethod = useCallback(async (companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getCompanyHasPaymentMethod: true }));

      const result = await api.getCompanyPaymentMethod(companyId);
      dispatch(setPaymentMethodConfigured(result));
      return result;
    } catch (e) {
        dispatch(setPaymentMethodConfigured(false));
        dispatch(setError(e));
        return false;
    } finally {
      dispatch(setLoading({ getCompanyHasPaymentMethod: false }));
    }
  }, []);

  const completeOnboarding = useCallback(async (companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ completeOnboarding: true }));

      await api.completeOnboarding(companyId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ completeOnboarding: false }));
    }
  }, []);

  const getOnboardingStatus = useCallback(async (companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ onboardingStatus: true }));

      const response = await api.getOnboardingStatus(companyId);
      dispatch(setOnboardingStatus(response.data));
      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ onboardingStatus: false }));
    }
  }, []);

  return {
    loading,
    error,
    createCompany,
    deleteCompany,
    getCompanyPaymentMethod,
    getStripeCompanyPortalLink,
    completeOnboarding,
    getOnboardingStatus,
  };
};
